import React from 'react';
import { styled } from '@compiled/react';
import {
	useRelayEnvironment,
	fetchQuery,
	graphql,
	type Environment,
	useFragment,
	useMutation,
} from 'react-relay';
import Heading from '@atlaskit/heading';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import UserPicker, { type OptionData, type Value, type User } from '@atlaskit/user-picker';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize, layers } from '@atlassian/jira-common-styles/src';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import { fg } from '@atlassian/jira-feature-gating';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { ui_softwareReleasesVersionDriverField_DriverField$key } from '@atlassian/jira-relay/src/__generated__/ui_softwareReleasesVersionDriverField_DriverField.graphql';
import type {
	ui_updateDriverMutation,
	ui_updateDriverMutation$data,
} from '@atlassian/jira-relay/src/__generated__/ui_updateDriverMutation.graphql';
import type { ui_versionDriverFieldQuery$data as Response } from '@atlassian/jira-relay/src/__generated__/ui_versionDriverFieldQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import {
	useUpdateDriverExperienceStart,
	useUpdateDriverExperienceFail,
	useUpdateDriverExperienceSuccess,
} from '../services/experience-tracker';
import messages from './messages';
import type { CommonProps, Props, PropsStateless } from './types';

const isOptionData = (val: OptionData | null): val is OptionData =>
	val != null && typeof val.name === 'string' && typeof val.id === 'string';

const createLoadQueryFunc =
	(environment: Environment, cloudId: string, projectAri: string) => (searchText?: string) =>
		fetchQuery(
			environment,
			graphql`
				query ui_versionDriverFieldQuery($projectAri: ID!, $searchText: String) {
					jira @required(action: THROW) {
						jiraProject(id: $projectAri) @required(action: THROW) {
							suggestedDriversForJiraVersion(searchText: $searchText) {
								edges {
									node @required(action: THROW) {
										accountId @required(action: THROW)
										name @required(action: THROW)
										picture
										... on AtlassianAccountUser {
											extendedProfile {
												jobTitle
											}
										}
									}
								}
							}
						}
					}
				}
			`,
			{
				projectAri,
				searchText,
			},
			{
				fetchPolicy: 'store-or-network',
			},
		)
			.toPromise()
			.then(
				// @ts-expect-error - TS2345 - Argument of type '(response: Response) => void' is not assignable to parameter of type '(value: unknown) => void | PromiseLike<void>'.
				(response: Response) => {
					const edges = response.jira.jiraProject.suggestedDriversForJiraVersion?.edges ?? [];
					return edges
						.filter(Boolean)
						.map((edge) => edge?.node)
						.map((user): OptionData | null =>
							user == null
								? null
								: // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
									({
										avatarUrl: user.picture,
										name: user.name,
										id: user.accountId,
										byline: user?.extendedProfile?.jobTitle ?? '',
										type: 'user',
									} as User),
						)
						.filter(isOptionData);
				},
			)
			.catch((error) => {
				fireErrorAnalytics({
					meta: {
						id: 'ui_versionDriverFieldQuery',
						packageName: 'jiraSoftwareReleasesVersionDriverField',
					},
					error,
				});

				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				return [] as OptionData[];
			});

export const DriverFieldStateless = ({
	width,
	projectId,
	initialDriverValue,
	onChange,
	isDisplayLabel = true,
	isRenderedInModal = false,
	menuPortalTargetRef,
	isDisabled = false,
	isDisplayBorder = false,
	ariaLabelledBy,
}: PropsStateless & CommonProps) => {
	const environment = useRelayEnvironment();
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();
	const menuPortalTarget = menuPortalTargetRef && menuPortalTargetRef.current;

	const loadOptions = createLoadQueryFunc(
		environment,
		cloudId,
		`ari:cloud:jira:${cloudId}:project/${projectId}`,
	);
	const experienceStart = useUpdateDriverExperienceStart();

	return (
		<Wrapper>
			{isDisplayLabel && <Heading level="h200">{formatMessage(messages.fieldLabel)}</Heading>}
			<PlacholderStyleOverrider isDisplayBorder={isDisplayBorder}>
				<UserPicker
					fieldId="jiraVersionDriver"
					{...(fg('jsw-roadmaps-timeline-releases-section-accessible') && {
						ariaLabelledBy,
					})}
					loadOptions={loadOptions}
					isDisabled={isDisabled}
					width={width}
					menuPortalTarget={isRenderedInModal ? menuPortalTarget || document.body : undefined}
					styles={
						isRenderedInModal && !menuPortalTarget
							? {
									menuPortal: (base) => ({ ...base, zIndex: 9999 }),
								}
							: undefined
					}
					menuMinWidth={33 * gridSize}
					defaultValue={initialDriverValue}
					placeholder={formatMessage(messages.unassigned)}
					onChange={(value: Value) => {
						// it is single select, so it should never be an array
						if (Array.isArray(value)) return;

						onChange?.(value == null ? null : value);
					}}
					onFocus={() => experienceStart()}
					onClear={() => experienceStart()}
				/>
			</PlacholderStyleOverrider>
		</Wrapper>
	);
};

export const DriverField = ({
	jiraVersion,
	width,
	onChange,
	isDisplayLabel = true,
	isRenderedInModal = false,
	isDisabled = false,
	isDisplayBorder = false,
	ariaLabelledBy,
}: Props & CommonProps) => {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagsService();
	const experienceFail = useUpdateDriverExperienceFail();
	const experienceSuccess = useUpdateDriverExperienceSuccess();

	const version = useFragment<ui_softwareReleasesVersionDriverField_DriverField$key>(
		graphql`
			fragment ui_softwareReleasesVersionDriverField_DriverField on JiraVersion {
				id @required(action: THROW)
				project @required(action: THROW) {
					projectId @required(action: THROW)
				}
				driverData @optIn(to: ["JiraVersionDriverResult"]) {
					driver {
						accountId @required(action: THROW)
						name @required(action: THROW)
						picture
					}
					queryError {
						message
					}
				}
			}
		`,
		jiraVersion,
	);

	const versionAri = version.id;
	const { projectId } = version.project;

	const onMutationError = (error: Error) => {
		experienceFail('graphql error', error);
		fireErrorAnalytics({
			meta: {
				id: 'jira.releases.release-modals-relay.create-release-modal',
				packageName: 'jiraSoftwareReleasesVersionDriverField',
				teamName: 'fusion-solaris',
			},
			error,
			sendToPrivacyUnsafeSplunk: true,
		});
		showFlag({
			title: formatMessage(messages.genericErrorFlagTitle),
			description: formatMessage(messages.genericErrorFlagDescription),
			type: 'error',
		});
	};
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticEvent = createAnalyticsEvent({
		action: 'updated',
		actionSubject: 'versionDriver',
	});
	const [updateDriverOnVersion] = useMutation<ui_updateDriverMutation>(graphql`
		mutation ui_updateDriverMutation($input: JiraUpdateVersionDriverInput!) {
			jira {
				updateJiraVersionDriver(input: $input) @optIn(to: ["JiraVersionUpdateDriver"]) {
					success
					errors {
						extensions {
							statusCode
							errorType
						}
						message
					}
				}
			}
		}
	`);

	const driverError = version.driverData?.queryError?.message;

	if (driverError != null) {
		throw new Error('Received QueryError when fetching driver');
	}

	const driver = version?.driverData?.driver;
	// eslint-disable-next-line no-console
	console.log('driver', driver);
	const currentlySetDriver: OptionData | undefined =
		driver == null
			? undefined
			: {
					avatarUrl: driver.picture,
					name: driver.name,
					id: driver.accountId,
				};

	return (
		<DriverFieldStateless
			width={width}
			{...(fg('jsw-roadmaps-timeline-releases-section-accessible') && {
				ariaLabelledBy,
			})}
			projectId={projectId}
			initialDriverValue={currentlySetDriver}
			isDisplayLabel={isDisplayLabel}
			isRenderedInModal={isRenderedInModal}
			isDisabled={isDisabled}
			isDisplayBorder={isDisplayBorder}
			onChange={(newDriver: OptionData | null) => {
				updateDriverOnVersion({
					variables: {
						input: {
							id: versionAri,
							driver: newDriver?.id,
						},
					},
					onCompleted: (data: ui_updateDriverMutation$data) => {
						if (data.jira?.updateJiraVersionDriver?.success !== true) {
							const responseErrors = data?.jira?.updateJiraVersionDriver?.errors;
							const errorMessage =
								responseErrors?.[0]?.message ?? 'updateJiraVersionDriver graphql mutation failed';
							onMutationError(new Error(errorMessage));
							return;
						}
						experienceSuccess();
						fireTrackAnalytics(analyticEvent, 'versionDriver updated', {
							isDriverSet: Boolean(newDriver?.id),
						});
					},
					onError: (error: Error) => {
						onMutationError(error);
					},
				});

				onChange?.(newDriver);
			}}
		/>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Wrapper = styled.div({
	width: '100%',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'stretch',
});

interface PlacholderStyleOverriderProps {
	isDisplayBorder: boolean;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PlacholderStyleOverrider = styled.div<PlacholderStyleOverriderProps>(
	{
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.fabric-user-picker__menu': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			zIndex: layers.modal,
		},
		/* FIXME: this style override is required for the placeholder styling to be compatible with react-select v4.
    Once JFE resolves to react-select v5, this style override can be removed (aka when the resolution for @atlaskit/select
    in the root package.json will be removed: https://ecosystem-platform.atlassian.net/browse/UNBOX-685). */
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.fabric-user-picker__value-container': {
			display: 'grid',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
			'.fabric-user-picker__placeholder': {
				position: 'relative',
				top: 'initial',
				left: 'initial',
				transform: 'initial',
			},
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'.fabric-user-picker__single-value': {
			width: '88%',
		},
	},
	// Remove this css hack when https://product-fabric.atlassian.net/browse/QS-2950 is done, or directly contribute to their code to support this natively.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ isDisplayBorder }) =>
		isDisplayBorder
			? {}
			: {
					marginLeft: token('space.negative.075', '-6px'),
					'.fabric-user-picker__control:not(.fabric-user-picker__control--menu-is-open):not(.fabric-user-picker__control--is-focused)':
						{
							border: 'none',
							background: 'none',
							'&:hover': {
								// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
								backgroundColor: token('color.background.input.hovered', colors.N20A),
							},
						},
				},
);
